"use client";

import { Github } from "lucide-react";
import { useEffect } from "react";
import { toast } from "sonner";
import { ErrorCode } from "@/generated/error-codes";
import { API_BASE_URL } from "@/shared/api-client";
import { Button } from "@/shared/components/ui/button";
import { cn } from "@/shared/lib/cn";
import type { SignInProviders } from "../api/auth-api";
import { useSignInProviders } from "../api/queries";
import { safeRedirectPath } from "../post-login-target";

/**
 * Social sign-in. Server-side OAuth code flow: the browser goes to the hub's
 * /auth/<provider> start, the hub round-trips the provider and sends the
 * browser to /oauth/callback with a one-time handoff, which that page trades
 * for the session. No tokens in JS.
 *
 * A provider this deployment has not set up shows its button disabled, with
 * one line saying so, never a dead click. A sign-in that failed at the
 * provider comes back here as ?error=<code> and reads as a sentence.
 */

type Provider = keyof SignInProviders;

const PROVIDER_LABEL: Record<Provider, string> = {
  google: "Google",
  microsoft: "Microsoft",
  github: "GitHub",
};

/** What a failed sign-in says, by the code the hub sent back. */
const SIGN_IN_ERRORS: Partial<Record<string, string>> = {
  [ErrorCode.AUTH_SIGN_IN_EMAIL_UNVERIFIED]:
    "That account's email isn't verified, so it can't sign you in.",
  [ErrorCode.AUTH_SIGN_IN_DOMAIN_NOT_ALLOWED]:
    "Your organization signs in with a different provider for this email domain.",
  [ErrorCode.AUTH_SIGN_IN_EXCHANGE_FAILED]: "The provider didn't complete the sign-in. Try again.",
  [ErrorCode.AUTH_SIGN_IN_PROVIDER_NOT_CONFIGURED]: "That sign-in option isn't set up yet.",
};

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="size-4" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M23.5 12.27c0-.85-.08-1.66-.22-2.45H12v4.64h6.45a5.52 5.52 0 0 1-2.4 3.62v3h3.88c2.27-2.09 3.57-5.17 3.57-8.81Z"
      />
      <path
        fill="#34A853"
        d="M12 24c3.24 0 5.96-1.07 7.94-2.91l-3.88-3c-1.08.72-2.46 1.15-4.06 1.15-3.12 0-5.77-2.11-6.71-4.95H1.28v3.1A12 12 0 0 0 12 24Z"
      />
      <path
        fill="#FBBC05"
        d="M5.29 14.29a7.21 7.21 0 0 1 0-4.58v-3.1H1.28a12 12 0 0 0 0 10.78l4.01-3.1Z"
      />
      <path
        fill="#EA4335"
        d="M12 4.76c1.76 0 3.34.6 4.58 1.79l3.44-3.44A11.98 11.98 0 0 0 12 0 12 12 0 0 0 1.28 6.61l4.01 3.1C6.23 6.87 8.88 4.76 12 4.76Z"
      />
    </svg>
  );
}

function MicrosoftIcon() {
  return (
    <svg viewBox="0 0 24 24" className="size-4" aria-hidden="true">
      <path fill="#F25022" d="M1 1h10v10H1z" />
      <path fill="#7FBA00" d="M13 1h10v10H13z" />
      <path fill="#00A4EF" d="M1 13h10v10H1z" />
      <path fill="#FFB900" d="M13 13h10v10H13z" />
    </svg>
  );
}

const PROVIDER_ICON: Record<Provider, React.ReactNode> = {
  google: <GoogleIcon />,
  microsoft: <MicrosoftIcon />,
  github: <Github className="size-4" />,
};

function startSignIn(provider: Provider) {
  // The page's ?redirect rides to the hub's start door (read at click time,
  // never at render, so the page stays statically prerenderable); the hub
  // carries it through the round trip in the OAuth state.
  const url = new URL(`${API_BASE_URL}/api/v1/auth/${provider}`);
  const path = safeRedirectPath(new URLSearchParams(window.location.search).get("redirect"));
  if (path) url.searchParams.set("redirect", path);
  window.location.href = url.toString();
}

export function SocialLoginButtonRow() {
  const { data: providers } = useSignInProviders();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("error");
    if (!code) return;
    toast.error("Sign-in didn't finish", {
      description: SIGN_IN_ERRORS[code] ?? "Something went wrong signing you in. Try again.",
    });
    params.delete("error");
    const query = params.toString();
    window.history.replaceState(null, "", `${window.location.pathname}${query ? `?${query}` : ""}`);
  }, []);

  // Until the listing answers, every button is enabled: the start door
  // refuses an unconfigured provider with a typed error anyway.
  const ready = (p: Provider) => providers?.[p] ?? true;
  const notSetUp = (["google", "microsoft", "github"] as const).filter((p) => !ready(p));

  const button = (provider: Provider, className?: string) => (
    <Button
      type="button"
      variant="outline"
      disabled={!ready(provider)}
      className={cn("h-11 rounded-xl border-border/70 text-[13.5px] font-medium", className)}
      onClick={() => startSignIn(provider)}
    >
      {PROVIDER_ICON[provider]}
      Continue with {PROVIDER_LABEL[provider]}
    </Button>
  );

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2.5">
        {button("google")}
        {button("microsoft")}
        {button("github", "col-span-2")}
      </div>
      {notSetUp.length > 0 && (
        <p className="text-center text-[12px] text-muted-foreground">
          {`${notSetUp.map((p) => PROVIDER_LABEL[p]).join(" and ")} sign-in isn't set up yet.`}
        </p>
      )}
    </div>
  );
}
